import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

export const SectionHeading = ({ overline, title, sub, center }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
    {overline && <span className="overline overline-light">{overline}</span>}
    <h2 className="font-display text-3xl md:text-4xl tracking-tight font-semibold mt-4 text-white leading-tight">
      {title}
    </h2>
    {sub && <p className="text-white/55 text-base md:text-lg leading-relaxed mt-5">{sub}</p>}
  </div>
);

export const PageHero = ({ overline, title, sub }) => (
  <section className="relative chrome pt-36 pb-20 md:pt-44 md:pb-28 radial-glow overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-60" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="overline overline-light">{overline}</span>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold mt-6 max-w-4xl leading-[1.05] text-white">
          {title}
        </h1>
        {sub && <p className="text-white/55 text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">{sub}</p>}
      </Reveal>
    </div>
  </section>
);

/* Page-level closing CTA — contextual navigation only. NO demo modal.
   Homepage has its own FinalCTA (the site-wide conversion moment). */
export const CTASection = ({
  title = "One operating layer. Every function of the school.",
  sub = "Continue exploring how PARTECON operates as a school-wide operating system.",
  primary = { label: "Explore Platform", to: "/platform" },
  secondary = { label: "How It Works", to: "/how-it-works" },
}) => (
  <section className="relative chrome py-24 md:py-32 overflow-hidden" data-testid="page-cta">
    <div className="absolute inset-0 radial-glow" />
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-semibold leading-tight text-white">
          {title}
        </h2>
        <p className="text-white/60 text-base md:text-lg mt-6 max-w-xl mx-auto">
          {sub}
        </p>
        <div className="flex flex-col sm:flex-row gap-3.5 justify-center mt-10">
          <Link to={primary.to} data-testid="cta-primary" className="btn-primary px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
            {primary.label} <ArrowRight size={15} />
          </Link>
          {secondary && (
            <Link to={secondary.to} data-testid="cta-secondary" className="btn-ghost px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
              {secondary.label}
            </Link>
          )}
        </div>
      </Reveal>
    </div>
  </section>
);

export const Marquee = ({ text }) => (
  <div className="relative overflow-hidden py-10 border-y border-white/10" data-testid="marquee">
    <div className="flex whitespace-nowrap animate-marquee">
      {[0, 1].map((k) => (
        <span key={k} className="font-display text-6xl md:text-8xl font-bold uppercase tracking-tight px-8"
          style={{ color: "transparent", WebkitTextStroke: "1px rgba(255,255,255,0.1)" }}>
          {text} · {text} ·&nbsp;
        </span>
      ))}
    </div>
  </div>
);

export const LaunchingSoon = ({ tone = "dark", label = "Launching Soon", className = "" }) => {
  const styles = tone === "dark"
    ? "border-white/15 bg-white/5 text-white/85"
    : "border-[#22C55E]/30 bg-[#22C55E]/8 text-[#166534]";
  return (
    <span
      data-testid="launching-soon-badge"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10.5px] font-mono-accent uppercase tracking-[0.16em] backdrop-blur ${styles} ${className}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F1BF26] opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#F1BF26]" />
      </span>
      {label}
    </span>
  );
};
